import { EntityAvatar } from "@web/components/EntityAvatar.tsx";
import { EmptyState } from "@web/components/EmptyState.tsx";
import { Skeleton } from "@web/components/ui/skeleton.tsx";
import { useOpenDetail } from "@web/features/detail/useOpenDetail.ts";
import { useArtistAlbums } from "@web/features/library/api.ts";

/** Simple artist albums in library: cover, name, year · track count. */
export function ArtistAlbumsSection({ artistId }: { artistId: string }) {
  const albums = useArtistAlbums(artistId);
  const openDetail = useOpenDetail();

  if (albums.isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (albums.isError) {
    return (
      <EmptyState
        title="Couldn't load albums"
        description={albums.error instanceof Error ? albums.error.message : "Unknown error"}
      />
    );
  }

  const list = albums.data ?? [];
  if (list.length === 0) {
    return (
      <EmptyState
        title="No albums in your library"
        description="No mirrored album rows for this artist yet."
      />
    );
  }

  return (
    <ul className="divide-y divide-line">
      {list.map((album) => {
        const year =
          album.release_date && album.release_date.length >= 4
            ? album.release_date.slice(0, 4)
            : null;
        return (
          <li key={album.id}>
            <button
              type="button"
              className="flex w-full items-center gap-3 px-1 py-2 text-left hover:bg-row-hover"
              onClick={() =>
                openDetail(
                  { type: "album", id: album.id, label: album.name ?? "Album" },
                  { mode: "push" },
                )
              }
            >
              <EntityAvatar src={album.cover_url} alt="" size="sm" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">
                  {album.name ?? "Unknown album"}
                </span>
                <span className="block truncate text-xs text-faint">
                  {[year, `${album.track_count.toLocaleString()} in library`]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
